// InspireIntroCard.tsx — a one-time tile, shown in the first module, that
// introduces the header "Inspire me" button. Replaces the old in-feed testimonial
// tiles: rather than scattering pull-quotes through the programme, we point people
// once to the reliable, on-demand button that surfaces the same words of others.
// A still of Craig sits on the left (the under-used "quote-card" frame), a faithful
// replica of the amber spark button anchors the copy, and the body says what it does.
import { Body, Btn, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { clipUrl } from '@/data/guideMessages';

export function InspireIntroCard({ onTry, onDismiss }: { onTry: () => void; onDismiss: () => void }) {
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 18,
        border: '1px solid var(--hair)',
        background: 'linear-gradient(180deg, var(--card2-top, var(--card-top)), var(--card2-bot, var(--card-bot)))',
        boxShadow: 'var(--edge), var(--sh1)',
        overflow: 'hidden',
        animation: 'fade .35s ease',
      }}
    >
      {/* dismiss */}
      <Pressable
        as="button"
        onClick={onDismiss}
        ariaLabel="Dismiss"
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 2,
          minWidth: 36,
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--ink-soft)',
        }}
      >
        <Icon name="chevDown" size={18} />
      </Pressable>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {/* still of Craig — left (transparent PNG/webp, shows the tile through) */}
        <img
          src={clipUrl('quote-card')}
          alt=""
          aria-hidden="true"
          style={{ flexShrink: 0, width: 104, height: 132, alignSelf: 'center', objectFit: 'contain', objectPosition: 'center', display: 'block' }}
        />

        {/* copy — right */}
        <div style={{ flex: 1, minWidth: 0, padding: '16px 16px 16px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
            {/* faithful replica of the header "Inspire me" button */}
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 999,
                border: '1px solid var(--hair)',
                background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--amber-deep)',
                boxShadow: 'var(--edge), var(--sh1)',
              }}
            >
              <Icon name="sparkle" size={16} />
            </span>
            <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 17, color: 'var(--ink)' }}>Inspire me</span>
          </div>
          <Body size={13.5} style={{ lineHeight: 1.6 }}>
            Tap the <strong style={{ color: 'var(--amber-deep)' }}>spark</strong> at the top of any screen, any time, to
            read a few honest words from someone who has walked this path — a small lift when you need one.
          </Body>
        </div>
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        <Btn full variant="soft" size="sm" onClick={onTry} leading={<Icon name="sparkle" size={16} />}>
          Try it now
        </Btn>
      </div>
    </div>
  );
}
